import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { 
  BookOpen, 
  FileText, 
  Sparkles, 
  CheckCircle2, 
  Download, 
  Library,
  ArrowRight,
  Zap,
  Shield,
  Users
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: FileText,
      title: "Upload Any Lesson",
      description: "Paste text directly or upload PDF, DOCX files. We extract and process your content automatically."
    },
    {
      icon: Sparkles,
      title: "AI-Powered Generation",
      description: "Our AI creates questions based solely on your passage, with evidence quotes for every answer."
    },
    {
      icon: CheckCircle2,
      title: "Teacher Review Mode",
      description: "Edit, delete, or regenerate individual questions. You're always in control of the final quiz."
    },
    {
      icon: Download,
      title: "Print-Ready Export",
      description: "Export quizzes as PDF or DOCX with clean worksheet formatting and separate answer keys."
    },
    {
      icon: Library,
      title: "Quiz Library",
      description: "Save, organize, duplicate, and edit your quizzes anytime. Build your assessment collection."
    },
    {
      icon: Zap,
      title: "Fast Generation",
      description: "Get your quiz ready in seconds, not hours. Spend more time teaching, less time creating assessments."
    }
  ];

  const questionTypes = [
    "Multiple Choice",
    "True/False", 
    "Short Answer",
    "Fill in the Blank"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(208,98,36,0.08)_0%,transparent_50%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-full text-sm font-medium text-primary mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered Quiz Generation
            </span>
            
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-primary leading-[1.1] mb-6">
              Turn Any Lesson Into a
              <span className="block text-accent"> Quiz in Seconds</span>
            </h1>
            
            <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto mb-10 leading-relaxed">
              Upload your lesson content, and our AI generates accurate, passage-based quizzes with explanations, 
              evidence quotes, and print-ready exports. Perfect for homeschool parents and teachers.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 relative z-20">
              <Button 
                size="lg" 
                onClick={() => navigate('/signup')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base rounded-xl shadow-lg hover:shadow-xl transition-all"
                data-testid="hero-get-started"
              >
                Start Creating Quizzes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => navigate('/login')}
                className="px-8 py-6 text-base rounded-xl border-2"
                data-testid="hero-login"
              >
                I Already Have an Account
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Question Types */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-3">
              Generate Multiple Question Types
            </h2>
            <p className="text-muted-foreground">Mix and match to create comprehensive assessments</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {questionTypes.map((type, index) => (
              <motion.div
                key={type}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white px-6 py-4 rounded-xl shadow-sm border border-border hover:border-accent/40 hover:shadow-md transition-all cursor-default"
              >
                <span className="font-medium text-foreground">{type}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-4">
              Everything You Need to Create Better Quizzes
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Built specifically for homeschool families, teachers, and tutoring centers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white border border-border rounded-xl p-8 hover:border-accent/40 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center mb-5 group-hover:bg-accent/10 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary group-hover:text-accent transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-medium text-primary mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-medium mb-4">
              How It Works
            </h2>
            <p className="text-primary-foreground/70 text-lg">Three simple steps to your perfect quiz</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Upload Your Lesson", desc: "Paste text or upload a PDF/DOCX file with your lesson content" },
              { step: "02", title: "Generate Quiz", desc: "Select question types, difficulty, and let AI create your quiz" },
              { step: "03", title: "Review & Export", desc: "Edit as needed, then export as a print-ready PDF or editable DOCX" }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center"
              >
                <div className="text-6xl font-serif font-medium text-accent/80 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-primary-foreground/70">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {[
              { icon: Shield, text: "Secure & Private" },
              { icon: Users, text: "Built for Educators" },
              { icon: Zap, text: "Fast Generation" },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 text-muted-foreground">
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-secondary rounded-2xl p-10 md:p-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-4">
              Ready to Save Hours on Quiz Creation?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Join teachers and homeschool parents who are creating better assessments in less time.
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/signup')}
              className="bg-primary hover:bg-primary/90 px-10 py-6 text-base rounded-xl"
              data-testid="cta-get-started"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col md:flex-row items-center justify-between w-full gap-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-primary-foreground" />
                </div>
                <span className="font-serif text-lg font-medium text-primary">HomeClass</span>
              </div>
              <div className="flex items-center gap-6">
                <Link to="/pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Pricing
                </Link>
                <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Terms
                </Link>
                <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Privacy
                </Link>
              </div>
              <p className="text-sm text-muted-foreground">
                © 2026 HomeClass. All rights reserved.
              </p>
            </div>
            <div className="text-sm text-muted-foreground">
              For support, contact us at{' '}
              <a 
                href="mailto:homeclassem@gmail.com" 
                className="text-primary hover:underline"
                data-testid="support-email-link"
              >
                homeclassem@gmail.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
